import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { TRolePermission } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import { Button, Select, Space, Table } from '@timeedit/ui-components/antd';
// REDUX
import { rolePermissionsSelector } from '../../slices/rolePermission.slice';

// TYPES
type Props = {
  rolePermissionIds: number[];
  onAdd: (rolePermissionId: number) => void;
  onRemove: (rolePermissionId: number) => void;
};

const RolePermissionTable = ({ rolePermissionIds, onAdd, onRemove }: Props) => {
  const allRolePermissions = useSelector(rolePermissionsSelector);
  const selectedRolePermissions = rolePermissionIds
    .filter(
      (rolePermissionId: number) =>
        allRolePermissions.findIndex(
          (rP: TRolePermission) => rP.id === rolePermissionId
        ) > -1
    )
    .map((rolePermissionId: number) =>
      allRolePermissions.find(
        (rP: TRolePermission) => rP.id === rolePermissionId
      )
    );

  const rolePermissionsToAdd = allRolePermissions.filter(
    (rP) => !rolePermissionIds.includes(rP.id)
  );

  const [rolePermissionIdToAdd, setRolePermissionIdToAdd] = useState<
    number | undefined
  >(undefined);

  const onAddRolePermission = () => {
    if (rolePermissionIdToAdd) {
      onAdd(rolePermissionIdToAdd);
      setRolePermissionIdToAdd(undefined);
    }
  };

  return (
    <>
      <Table
        columns={[
          {
            title: 'Group',
            dataIndex: 'rolePermissionGroup',
            key: 'rolePermissionGroup',
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
          },
          {
            title: 'Enabled actions',
            dataIndex: 'groupActions',
            key: 'groupActions',
            render: (value: string[]) => value.join(', '),
          },
          {
            title: 'Actions',
            dataIndex: 'id',
            key: 'actions',
            render: (rolePermissionId: number) => (
              <Button
                size="small"
                type="link"
                danger
                onClick={() => onRemove(rolePermissionId)}
              >
                <Icon id="DeleteOutlined" alt="Delete" />
              </Button>
            ),
          },
        ]}
        dataSource={[...selectedRolePermissions]}
        rowKey="id"
        pagination={false}
        footer={() => (
          <Space direction="horizontal">
            <Select
              size="small"
              value={rolePermissionIdToAdd}
              placeholder="Select a role permission"
              onChange={setRolePermissionIdToAdd}
              style={{ width: '200px' }}
            >
              {(rolePermissionsToAdd || []).map((rP) => (
                <Select.Option
                  key={rP.id}
                  value={rP.id}
                >{`${rP.rolePermissionGroup} - ${rP.name}`}</Select.Option>
              ))}
            </Select>
            <Button
              size="small"
              type="primary"
              onClick={() => onAddRolePermission()}
              disabled={!rolePermissionIdToAdd}
            >
              + Add
            </Button>
          </Space>
        )}
      />
    </>
  );
};

export default RolePermissionTable;
